import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Users, Building2, FileText, Target, TrendingUp, Activity, DollarSign, CheckCircle, Clock } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell } from 'recharts';
import { COLORS, formatCurrency } from './adminUtils';

const StatCard = ({ label, value, icon: Icon, iconColor, sub }) => (
  <Card className="bg-slate-900/50 border-slate-800">
    <CardContent className="py-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-slate-400">{label}</p>
          <p className="text-2xl font-bold text-white">{value}</p>
        </div>
        <Icon className={`h-6 w-6 ${iconColor}`} />
      </div>
      {sub}
    </CardContent>
  </Card>
);

export const AdminDashboardTab = ({ stats, detailedStats, language }) => {
  if (!stats) return null;
  const tr = language === 'tr';

  const userTypeData = detailedStats?.users_by_role
    ? Object.entries(detailedStats.users_by_role).map(([role, count]) => ({
        name: role === 'admin' ? 'Admin' : role === 'tenant_admin' ? 'Tenant Admin' : 'User',
        value: count
      }))
    : [];

  const assessmentStatusData = detailedStats?.assessments_by_status
    ? Object.entries(detailedStats.assessments_by_status).map(([status, count]) => ({
        name: status === 'completed' ? (tr ? 'Tamamlandı' : 'Completed') : (tr ? 'Devam Eden' : 'In Progress'),
        value: count
      }))
    : [];

  return (
    <div className="space-y-6" data-testid="admin-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard label={tr ? 'Toplam Üye' : 'Total Users'} value={stats.total_users} icon={Users} iconColor="text-blue-400"
          sub={<p className="text-xs text-emerald-400 mt-1">+{stats.new_users_30d} (30g)</p>} />
        <StatCard label={tr ? 'Şirket' : 'Companies'} value={stats.total_companies} icon={Building2} iconColor="text-purple-400" />
        <StatCard label={tr ? 'Değerlendirme' : 'Assessments'} value={stats.total_assessments} icon={Target} iconColor="text-amber-400"
          sub={<p className="text-xs text-slate-400 mt-1">{stats.completed_assessments} {tr ? 'tamamlandı' : 'done'}</p>} />
        <StatCard label={tr ? 'Tamamlanma' : 'Completion'} value={`${stats.completion_rate}%`} icon={Activity} iconColor="text-emerald-400" />
        <StatCard label={tr ? 'Ort. GDMII' : 'Avg GDMII'} value={stats.avg_gdmii_score} icon={TrendingUp} iconColor="text-cyan-400" />
        <StatCard label={tr ? 'Rapor' : 'Reports'} value={stats.total_reports} icon={FileText} iconColor="text-rose-400" />
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm">{tr ? 'Üye Tipleri' : 'User Types'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <RechartsPieChart>
                  <Pie data={userTypeData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}
                    label={({ name, value }) => `${name}: ${value}`}>
                    {userTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm">{tr ? 'Değerlendirme Durumu' : 'Assessment Status'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <RechartsPieChart>
                  <Pie data={assessmentStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70} paddingAngle={2}
                    label={({ name, value }) => `${name}: ${value}`}>
                    <Cell fill="#10b981" />
                    <Cell fill="#f59e0b" />
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm">{tr ? 'Paket Dağılımı' : 'Tier Distribution'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                <BarChart data={Object.entries(stats.assessments_by_tier || {}).map(([tier, count]) => ({
                  name: tier.replace('_', ' ').substring(0, 8),
                  count
                }))}>
                  <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {detailedStats && (
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-amber-400" />
                <CardTitle className="text-white text-sm">{tr ? 'Yarım Kalan Değerlendirmeler' : 'Incomplete Assessments'}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-amber-400 mb-2">{detailedStats.incomplete_assessments || 0}</p>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {detailedStats.recent_incomplete?.slice(0, 5).map((a, i) => (
                  <div key={i} className="flex justify-between text-xs">
                    <span className="text-slate-400 truncate">{a.company_name}</span>
                    <span className="text-slate-500">{a.progress}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-emerald-400" />
                <CardTitle className="text-white text-sm">{tr ? 'Bugün Tamamlanan' : 'Completed Today'}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-emerald-400 mb-2">{detailedStats.completed_today || 0}</p>
              <p className="text-xs text-slate-400">{tr ? 'Bu hafta: ' : 'This week: '}{detailedStats.completed_this_week || 0}</p>
              <p className="text-xs text-slate-400">{tr ? 'Bu ay: ' : 'This month: '}{detailedStats.completed_this_month || 0}</p>
            </CardContent>
          </Card>

          <Card className="bg-slate-900/50 border-slate-800">
            <CardHeader className="pb-2">
              <div className="flex items-center space-x-2">
                <DollarSign className="h-4 w-4 text-cyan-400" />
                <CardTitle className="text-white text-sm">{tr ? 'Gelir Özeti' : 'Revenue Summary'}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-cyan-400 mb-2">{formatCurrency(detailedStats.total_revenue || 0)}</p>
              <p className="text-xs text-slate-400">{tr ? 'Bu ay: ' : 'This month: '}{formatCurrency(detailedStats.revenue_this_month || 0)}</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
